import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  Target, 
  TrendingUp, 
  Building2, 
  CheckCircle2, 
  ArrowRight,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Partner logos - all from original site + new uploads
const partnerLogos = [
  // New uploads from customer assets
  { name: 'Avdoo', url: 'https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/5zzsa5yt_image0.jpeg', link: null },
  { name: 'Monadnock Construction', url: 'https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/a0rzxcd8_logo_moncon_181219.PNG', link: null },
  { name: 'Milrose Consultants', url: 'https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/3yp0rq7v_Milrose%20Logo%20%5BPRINT%5D-Horizontal.jpg', link: null },
  { name: 'JG Funding', url: 'https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/v653yfx0_JG%20Funding%20HI%20RES%20v1%20PLAIN.jpg', link: 'http://www.jgfunding.com/' },
  // Local logos from old website
  { name: 'JM Zoning', url: '/logos/jm_zoning.png', link: 'http://www.jmzoning.com/' },
  { name: 'TFJ Contracting', url: '/logos/TFJ_Contracting.png', link: null, size: 'large' },
  { name: 'Chein Insurance', url: '/logos/chein.jpg', link: 'https://www.chein-insurance.com/' },
  { name: 'Jolt', url: '/logos/jolt.png', link: 'https://jolt.dev/' },
  { name: 'Isaac Stern', url: '/logos/issac_stern.png', link: 'http://issacstern.com/index.html' },
  { name: 'MedServ', url: '/logos/medserv.jpg', link: 'https://www.themedservgroup.com/' },
  { name: 'DentServ', url: '/logos/dentserv.jpg', link: 'http://www.dentserv.com/' },
  { name: 'Vision', url: '/logos/vision.png', link: 'https://www.vfgcpas.com/' },
  { name: 'Icon Realty', url: '/logos/icon_realty.jpg', link: 'http://www.iconrealtymgmt.com/', size: 'xlarge' },
  { name: 'Clipper Equity', url: '/logos/clipper.png', link: 'http://www.clipperequity.com/', size: 'large' },
  { name: 'Gutman Weiss', url: '/logos/gutman_weiss.PNG', link: null, size: 'large' },
  { name: 'HASC', url: '/logos/HASC_Logo.png', link: 'https://www.hasccenter.org/' },
  { name: 'Prospr', url: '/logos/PROSPR_logo.png', link: 'https://prospr.fit/' },
  { name: 'Ani Tefillah', url: '/logos/ani_tefillah.jpg', link: 'https://www.anitefillah.org/' },
  { name: 'DBI', url: '/logos/dbi.png', link: 'http://cc.dbinyc.com/', invert: true },
  { name: 'DG Mechanical', url: '/logos/dg_mechanical.PNG', link: null },
  { name: 'BLVS', url: '/logos/BLVS_logo_.png', link: null },
];

// Partner Logo Component - clean and professional
const PartnerLogoCard = ({ partner, index }) => {
  const [hasError, setHasError] = useState(false);
  
  const showText = hasError || partner.textOnly || !partner.url;
  
  // Determine size class based on partner.size property
  let sizeClass = 'h-10';
  let maxWidth = '120px';
  
  if (partner.size === 'xlarge') {
    sizeClass = 'h-16';
    maxWidth = '160px';
  } else if (partner.size === 'large') {
    sizeClass = 'h-14';
    maxWidth = '150px';
  } else if (partner.size === 'small') {
    sizeClass = 'h-8';
    maxWidth = '100px';
  }
  
  const content = showText ? (
    <span className="text-slate-800 font-semibold text-sm tracking-tight">{partner.name}</span>
  ) : (
    <img 
      src={partner.url} 
      alt={partner.name}
      className={`${sizeClass} w-auto object-contain transition-transform duration-300 group-hover:scale-110 ${partner.invert ? 'invert' : ''}`}
      style={{ maxWidth }}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
  
  const wrapper = (
    <div className="group flex items-center justify-center h-16 px-3 transition-all duration-300 hover:bg-slate-100 rounded-lg cursor-pointer">
      {content}
    </div>
  );
  
  return (
    <div data-testid={`partner-logo-${index}`}>
      {partner.link ? (
        <a href={partner.link} target="_blank" rel="noopener noreferrer">
          {wrapper}
        </a>
      ) : (
        wrapper
      )}
    </div>
  );
};

const features = [
  {
    icon: Users,
    title: 'Experienced Professionals',
    description: 'Candidates with proven track records, leadership, and real-world problem solving.',
  },
  {
    icon: Target,
    title: 'Precision Matching',
    description: 'Skills, culture, and goals aligned intentionally—not randomly.',
  },
  {
    icon: TrendingUp,
    title: 'Economic Impact',
    description: 'Helping heads of households achieve financial stability while strengthening the community economy.',
  },
  {
    icon: Building2,
    title: 'Confidence-Backed Hiring',
    description: 'Employers may be eligible for short-term wage support to ease onboarding while we provide hands-on placement guidance to ensure long-term success.',
  },
];

const employerBenefits = [
  'Pre-vetted candidates with deep experience',
  'Human screening beyond ATS filters',
  'Hands-on coordination from intro to onboarding',
  'A partner invested in long-term success',
];

const candidateBenefits = [
  'Professional résumé positioning',
  'Interview coaching that builds confidence',
  'Direct employer introductions',
  'Personalized career coaching',
];

const stats = [
  { value: 36, suffix: '', label: 'Placements' },
  { value: 150, suffix: '+', label: 'Hiring & Recruiting Partners' },
  { value: 95, suffix: '%', label: 'Retention Rate' },
  { value: 3500, prefix: '$', suffix: '+', label: 'Annual Salary Restored', formatAsMoney: true },
];

// Format large numbers for money display - always consistent format
const formatNumber = (num, formatAsMoney = false) => {
  if (formatAsMoney) {
    if (num >= 1000) {
      const millions = num / 1000;
      if (millions >= 3.5) return '3.5M';
      if (millions >= 3) return '3M';
      if (millions >= 2) return '2M';
      if (millions >= 1.9) return '1.9M';
      if (millions >= 1.5) return '1.5M';
      if (millions >= 1) return '1M';
      return Math.floor(millions * 10) / 10 + 'M';
    }
    if (num >= 100) {
      return Math.floor(num) + 'K';
    }
    return num + 'K';
  }
  return num;
};

// Custom hook for animated counter - isolated state management
const useAnimatedCounter = (targetValue, isVisible, duration = 1200) => {
  const [currentValue, setCurrentValue] = useState(0);
  const animationRef = useRef(null);
  const hasStartedRef = useRef(false);
  const startTimeRef = useRef(null);

  useEffect(() => {
    // Only start animation once when becoming visible
    if (!isVisible || hasStartedRef.current) return;
    
    hasStartedRef.current = true;
    
    const easeOutQuad = (t) => t * (2 - t);
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }
      
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuad(progress);
      
      setCurrentValue(Math.round(easedProgress * targetValue));
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at target value
        setCurrentValue(targetValue);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, targetValue, duration]);

  return currentValue;
};

// Individual stat component with its own animation
const AnimatedStat = ({ stat, index, isVisible }) => {
  const animatedValue = useAnimatedCounter(stat.value, isVisible);
  
  return (
    <div 
      className="text-center stat-number"
      data-testid={`stat-${index}`}
    >
      <div 
        className="text-3xl md:text-4xl font-bold text-slate-900 mb-1 tabular-nums"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {stat.prefix || ''}
        {formatNumber(animatedValue, stat.formatAsMoney)}
        {stat.suffix || ''}
      </div>
      <div className="text-sm text-slate-600">{stat.label}</div>
    </div>
  );
};

// Synced animated counters component - uses individual stat components
const AnimatedStats = ({ stats, isVisible }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <AnimatedStat 
          key={index} 
          stat={stat} 
          index={index} 
          isVisible={isVisible} 
        />
      ))}
    </div>
  );
};

export default function Home() {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToStats = () => {
    const statsSection = document.getElementById('stats');
    if (statsSection) {
      statsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-slate-800 flex items-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-slate-900/70" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl pt-24 pb-16">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Experienced Talent.{' '}
              <span className="text-purple-200 italic">Thoughtfully Matched.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              We are a nonprofit recruitment firm that connects organizations with high-caliber, <span className="whitespace-nowrap">mid-career</span> professionals, while guiding candidates through career transitions with professionalism, strategy, dignity, and heart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <Link to="/employers" data-testid="hero-employer-btn">
                <Button 
                  className="btn-secondary bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 py-7 text-xl font-medium"
                >
                  I'm an Employer
                </Button>
              </Link>
              <Link to="/candidates" data-testid="hero-candidate-btn">
                <Button 
                  className="btn-primary bg-slate-700 hover:bg-slate-600 text-white rounded-full px-10 py-7 text-xl font-medium"
                >
                  I'm a Candidate
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button 
          onClick={scrollToStats}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 hover:text-white cursor-pointer scroll-indicator"
          aria-label="Scroll to stats"
          data-testid="scroll-indicator"
        >
          <span className="text-sm mb-2 block">Scroll</span>
          <ChevronDown className="w-6 h-6 mx-auto" />
        </button>
      </section>

      {/* Impact Stats - Now right after hero */}
      <section id="stats" ref={statsRef} className="py-16 md:py-20 bg-white" data-testid="stats-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-6">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Our Impact
            </h2>
            <p className="text-base text-slate-600 mt-3 max-w-2xl mx-auto">
              Every placement strengthens organizations and restores stability for families.
            </p>
          </div>
          
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest text-center mb-6">
            2025 Impact
          </p>

          <AnimatedStats stats={stats} isVisible={statsVisible} />
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-16 md:py-20 bg-slate-50" data-testid="mission-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-2">
              Our Mission
            </p>
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Unlocking Opportunity Through Meaningful Work
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Employment is more than a paycheck: it's purpose, stability, and pride. We connect 
              organizations with proven professionals while helping talented people translate their 
              experience into new opportunity.
            </p>
            <p className="text-base text-slate-900 font-bold max-w-2xl mx-auto mt-3">
              Because experience is an asset, not a liability.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`card-hover p-6 rounded-xl border border-slate-100 ${
                  index === 3 ? 'bg-slate-900 text-white' : 'bg-white'
                }`}
                data-testid={`feature-card-${index}`}
              >
                <feature.icon 
                  className={`w-7 h-7 mb-3 ${index === 3 ? 'text-purple-200' : 'text-slate-700'}`} 
                />
                <h3 
                  className={`text-lg font-semibold mb-2 ${index === 3 ? 'text-white' : 'text-slate-900'}`}
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${index === 3 ? 'text-slate-300' : 'text-slate-600'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Employers & Candidates Split Section */}
      <section className="py-0" data-testid="split-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Employers */}
          <div className="bg-white p-10 md:p-12 lg:p-16">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
              For Employers
            </p>
            <h3 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-3"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Hire Seasoned Talent, Done Right.
            </h3>
            <p className="text-slate-600 mb-5 text-sm">
              Access professionals who bring maturity, perspective, and immediate value—supported 
              by a process built for quality and fit.
            </p>
            <ul className="space-y-2 mb-5">
              {employerBenefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-slate-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-sm">{benefit}</span>
                </li>
              ))}
            </ul>
            
            {/* Our Process */}
            <div className="border-t border-slate-200 pt-5 mt-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
                How We Work With Employers
              </p>
              <div className="space-y-2">
                {[
                  'Understand role & culture',
                  'Human vetting and candidate preparation',
                  'Curated introductions',
                  '90-day success check-ins',
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-4 h-4 text-slate-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Link to="/employers" data-testid="employers-cta" className="block mt-6">
              <Button className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-full px-10 py-6 text-xl">
                Find Talent
              </Button>
            </Link>
          </div>

          {/* Candidates */}
          <div className="bg-slate-800 p-10 md:p-12 lg:p-16">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
              For Candidates
            </p>
            <h3 
              className="text-2xl md:text-3xl font-bold text-white mb-3"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              We Help You Tell Your Real Story.
            </h3>
            <p className="text-slate-300 mb-6 text-sm">
              You've built a career. We help you translate it for today's market: with clarity, 
              preparation, and direct access to employers who value experience.
            </p>
            <ul className="space-y-3 mb-6">
              {candidateBenefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-purple-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200 text-sm">{benefit}</span>
                </li>
              ))}
            </ul>
            <Link to="/candidates" data-testid="candidates-cta">
              <Button className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 py-6 text-xl">
                Apply Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="pt-16 md:pt-20 pb-8 md:pb-10 bg-white" data-testid="partners-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Organizations That Value Proven Professionals
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {partnerLogos.map((partner, index) => (
              <PartnerLogoCard key={index} partner={partner} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Network Reach Section */}
      <section className="pb-16 md:pb-20 bg-white" data-testid="network-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="border-t border-slate-200 pt-8 md:pt-10">
            <div className="text-center mb-6 animate-fade-in-up">
              <h3 
                className="text-xl md:text-2xl font-bold text-slate-900 mb-3"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Where Our Network Reaches
              </h3>
              <p className="text-base text-slate-600 leading-relaxed max-w-3xl mx-auto mb-4">
                Our network includes professionals at organizations across a wide range of industries that help us make connections, identify opportunities, and get strong candidates in front of the right people.
              </p>
              <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                Some organizations represented across our network include:
              </p>
            </div>
            <p className="text-center text-base text-slate-600 leading-loose max-w-4xl mx-auto animate-fade-in-up animation-delay-200">
              <span className="font-medium text-slate-700">AlphaSense</span> • <span className="font-medium text-slate-700">Amazon</span> • <span className="font-medium text-slate-700">AMG Companies</span> • <span className="font-medium text-slate-700">AmTrust</span> • <span className="font-medium text-slate-700">Ash & Parsont LLP</span> • <span className="font-medium text-slate-700">B&H</span> • <span className="font-medium text-slate-700">Center Education Group</span> • <span className="font-medium text-slate-700">Centers HealthCare</span> • <span className="font-medium text-slate-700">Cross River Bank</span> • <span className="font-medium text-slate-700">DoorDash</span> • <span className="font-medium text-slate-700">Elevance Health</span> • <span className="font-medium text-slate-700">Flowcode</span> • <span className="font-medium text-slate-700">Goldman Sachs</span> • <span className="font-medium text-slate-700">JCCSI</span> • <span className="font-medium text-slate-700">Jefferies</span> • <span className="font-medium text-slate-700">JPMorgan Chase</span> • <span className="font-medium text-slate-700">KBRA</span> • <span className="font-medium text-slate-700">LBAPS</span> • <span className="font-medium text-slate-700">Makor</span> • <span className="font-medium text-slate-700">monday.com</span> • <span className="font-medium text-slate-700">MVB Financial</span> • <span className="font-medium text-slate-700">National Funding</span> • <span className="font-medium text-slate-700">Ohel</span> • <span className="font-medium text-slate-700">Optum</span> • <span className="font-medium text-slate-700">Orthodox Union</span> • <span className="font-medium text-slate-700">Palo Alto Networks</span> • <span className="font-medium text-slate-700">PwC</span> • <span className="font-medium text-slate-700">Sky Wire Networks</span> • <span className="font-medium text-slate-700">TD Bank</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
